import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import {
  AttachClaimsToBatchUploadInput,
  BatchUploadType,
  useAttachClaimsToBatchUploadMutation,
  useCreateBatchMutation,
} from '../../../../api/gql';
import { FullPageLoader } from '../../../../components';
import { useClaimsUploadProcessController } from '../../../../hooks/useClaimsUploadProcessController';
import { useXmlDataStore } from '../../../../hooks/useXmlDataStore';
import { notify } from '../../../../utils/notification';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';

const TRANS_NS = 'claims-upload';

export const ClaimsCreateBatch = () => {
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();

  const { xmlDataModel } = useXmlDataStore();
  const { updateProcess } = useClaimsUploadProcessController();

  const { mutate } = useCreateBatchMutation({
    onSuccess: ({ createBatchUpload: { id } }) => {
      xmlDataModel.batchId = id;
      attachClaimsToBatchUpload(id);
    },
    onError: () => {
      notify.negative({ message: t('createBatch.error') });
      //back to summary page - batch could not be created
      safeNavigate('/upload/claims/summary');
    },
  });

  const { mutate: attachClaimsToBatch } = useAttachClaimsToBatchUploadMutation({
    onSuccess: () => {
      if (!xmlDataModel.hasInvoicesForBilling) {
        // there is no invoices to pay
        safeNavigate('/');
        notify.neutral({ message: t('createBatch.nothingToPay') });
        return;
      }
      updateProcess();
    },
    onError: () => {
      notify.negative({ message: t('createBatch.error') });
      //back to summary page - batch could not be created
      safeNavigate('/upload/xml/summary');
    },
  });

  const attachClaimsToBatchUpload = (batchUploadId: string) => {
    const payload: AttachClaimsToBatchUploadInput['claims'] = xmlDataModel.xmlInvoicesForUpload.map((invoice) => {
      const customer = xmlDataModel.xmlCustomers.find((c) => c.invoices.includes(invoice.id));
      const vendor = xmlDataModel.xmlVendors.find((v) => v.invoices.includes(invoice.id));
      return {
        amount: invoice.details.amount ?? 0,
        amountDue: invoice.details.amountDue ?? 0,
        bankAccount: invoice.details.bankAccount?.toString(),
        bankNumber: invoice.details.bankNumber?.toString(),
        name: invoice.details.name ?? '',
        dueDate: invoice.details.dueDate ?? '',
        number: invoice.details.number?.toString() ?? '',
        issueDate: invoice.details.issueDate,
        variableSymbol: invoice.details.variableSymbol?.toString(),
        currency: invoice.details.currency ?? '',
        customer: {
          name: customer?.details.name ?? '',
          email: customer?.details.email,
          phoneNumber: customer?.details.phoneNumber,
        },
        vendor: {
          name: vendor?.details.name ?? '',
        },
      };
    });

    attachClaimsToBatch({
      input: {
        claims: payload,
        batchUploadId: batchUploadId,
        scenarioId: xmlDataModel.scenarioId,
      },
    });
  };

  useEffectOnce(() => {
    mutate({
      input: {
        type: BatchUploadType.Export,
      },
    });
  });

  return <FullPageLoader />;
};
